import { State, Action, StateContext, Selector } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { DashboardActions } from './dashboard.actions';
import { DashboardService } from '../../services/dashboard/dashboard.service';
import { tap, catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import {
  IGetMetrics,
  IGetLineChart,
  IGetPieChart,
} from '../../interfaces/dashboard.interface';

export interface DashboardStateModel {
  loading: boolean;
  error: any | null;
  metrics: IGetMetrics | null;
  chartLine: IGetLineChart | null;
  chartPie: IGetPieChart | null;
}

@State<DashboardStateModel>({
  name: 'dashboard',
  defaults: {
    loading: false,
    error: null,
    metrics: null,
    chartLine: null,
    chartPie: null,
  },
})

@Injectable()
export class DashboardState {
  constructor(private dashboardService: DashboardService) {}

  @Selector()
  static getLoading(state: DashboardStateModel) {
    return state.loading;
  }

  @Selector()
  static getMetrics(state: DashboardStateModel): IGetMetrics | null {
    return state.metrics;
  }

  @Selector()
  static getChartLine(state: DashboardStateModel): IGetLineChart | null {
    return state.chartLine;
  }

  @Selector()
  static getChartPie(state: DashboardStateModel): IGetPieChart | null {
    return state.chartPie;
  }

  @Selector()
  static getError(state: DashboardStateModel) {
    return state.error;
  }

  @Action(DashboardActions.GetDashboard)
  getDashboard(ctx: StateContext<DashboardStateModel>, action: DashboardActions.GetDashboard) {
    ctx.patchState({ loading: true, error: null });
    return this.dashboardService.getDashboard(action.startDate, action.endDate).pipe(
      tap((metrics) => ctx.patchState({ loading: false, metrics: metrics.data })),
      catchError((error) => {
        ctx.dispatch(new DashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      })
    );
  }

  @Action(DashboardActions.GetDashboardLine)
  getDashboardLine(ctx: StateContext<DashboardStateModel>, action: DashboardActions.GetDashboardLine) {
    return this.dashboardService.getDasboardLine(action.startDate, action.endDate).pipe(
      tap((chartLine) => ctx.patchState({ chartLine: chartLine.data })),
      catchError((error) => {
        ctx.dispatch(new DashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      })
    );
  }

  @Action(DashboardActions.GetDashboardPie)
  getDashboardPie(ctx: StateContext<DashboardStateModel>, action: DashboardActions.GetDashboardPie) {
    return this.dashboardService.getDasboardPie(action.startDate, action.endDate).pipe(
      tap((chartPie) => ctx.patchState({ chartPie: chartPie.data })),
      catchError((error) => {
        ctx.dispatch(new DashboardActions.ErrorDashboard(error));
        return throwError(() => error);
      })
    );
  }

  @Action(DashboardActions.ErrorDashboard)
  errorDashboard(ctx: StateContext<DashboardStateModel>, action: DashboardActions.ErrorDashboard) {
    ctx.patchState({ loading: false, error: action.error });
  }
}
